import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useIsMounted from '../../shared/hooks/useIsMounted';
import {
  decorateEdgesWithValidation,
  decorateNodesWithValidation,
} from '../utils/applyGraphValidation';
import {
  buildPublishBarWarningItems,
  buildValidationListItems,
  dedupeValidationListItems,
} from '../utils/workflowWarningItems';
import {
  getEdgeValidationMap,
  getStepValidationMap,
  getViolationFocusTarget,
  hasBlockingPreflightErrors,
  mergeValidationDetails,
  validateWorkflowPreflight,
} from '../utils/workflowValidation';

// Publishing an unscoped workflow is allowed and silently useless: it goes live matching no
// asset, so it never appears in Assign Task and no field engineer can self-start it. The
// workflow list has had a "Needs scoping" filter all along -- the condition was known, it just
// was not raised at the moment it matters. A new workflow now starts scoped to Global, so
// reaching this prompt means the scope was cleared on purpose, which is why it asks rather
// than blocks.
const UNSCOPED_PUBLISH_PROMPT =
  'This workflow has no scope, so publishing it will not make it visible on any asset: '
  + 'it will not appear in Assign Task and cannot be self-started. '
  + 'Add a tag, or turn on Global, to make it reachable.\n\nPublish anyway?';

export default function useWorkflowEditorValidation({
  workflow,
  forms,
  formsLoading,
  validateWorkflow,
  publishWorkflow,
  setNodes,
  setEdges,
  fitView,
  setSelectedItem,
  lastClickedNodeRef,
}) {
  const isMounted = useIsMounted();
  const [publishValidationError, setPublishValidationError] = useState(null);
  const [preflightWarnings, setPreflightWarnings] = useState([]);
  const [serverValidationDetails, setServerValidationDetails] = useState([]);
  const [showPublishErrorBar, setShowPublishErrorBar] = useState(false);

  const workflowRef = useRef(workflow);
  workflowRef.current = workflow;

  const validateWorkflowRef = useRef(validateWorkflow);
  validateWorkflowRef.current = validateWorkflow;

  const lastServerDetailsKeyRef = useRef('');

  const workflowStepsKey = useMemo(
    () => (Array.isArray(workflow?.workflow_steps) ? JSON.stringify(workflow.workflow_steps) : ''),
    [workflow?.workflow_steps]
  );

  const knownFormSlugsKey = useMemo(() => {
    if (formsLoading) return '';
    return (forms || [])
      .map((f) => f.slug)
      .filter(Boolean)
      .sort()
      .join('\0');
  }, [forms, formsLoading]);

  useEffect(() => {
    if (!workflowStepsKey) {
      setPreflightWarnings((prev) => (prev.length === 0 ? prev : []));
      return;
    }
    const steps = workflowRef.current?.workflow_steps;
    if (!Array.isArray(steps)) return;

    const knownFormSlugs = new Set((forms || []).map((f) => f.slug).filter(Boolean));
    const next = validateWorkflowPreflight(steps, {
      knownFormSlugs,
      formsLoading,
      mode: 'publish',
    });
    const nextKey = JSON.stringify(next);
    setPreflightWarnings((prev) => (JSON.stringify(prev) === nextKey ? prev : next));
  }, [workflowStepsKey, knownFormSlugsKey, forms, formsLoading]);

  useEffect(() => {
    lastServerDetailsKeyRef.current = '';
  }, [workflow?.id]);

  useEffect(() => {
    if (!workflow?.id || !workflowStepsKey) {
      setServerValidationDetails((prev) => (prev.length === 0 ? prev : []));
      return undefined;
    }

    const timer = window.setTimeout(() => {
      const wf = workflowRef.current;
      if (!wf?.id || !Array.isArray(wf.workflow_steps)) return;

      validateWorkflowRef.current(wf, { mode: 'publish' })
        .then((result) => {
          if (!isMounted.current) return;
          const details = Array.isArray(result?.details) ? result.details : [];
          const detailsKey = JSON.stringify(details);
          if (detailsKey === lastServerDetailsKeyRef.current) return;
          lastServerDetailsKeyRef.current = detailsKey;
          setServerValidationDetails(details);
        })
        .catch((err) => {
          if (!isMounted.current) return;
          console.error('Workflow validate request failed:', err);
          lastServerDetailsKeyRef.current = '';
          setServerValidationDetails((prev) => (prev.length === 0 ? prev : []));
        });
    }, 1000);

    return () => window.clearTimeout(timer);
  }, [workflow?.id, workflowStepsKey, isMounted]);

  useEffect(() => {
    if (!showPublishErrorBar || !publishValidationError) return;
    if (preflightWarnings.length > 0) {
      setPublishValidationError(null);
    }
  }, [preflightWarnings, showPublishErrorBar, publishValidationError]);

  useEffect(() => {
    if (!showPublishErrorBar) return;
    const hasLocalWarnings = preflightWarnings.length > 0;
    const hasServerWarnings = (publishValidationError?.messages || []).length > 0;
    if (!hasLocalWarnings && !hasServerWarnings) {
      setShowPublishErrorBar(false);
    }
  }, [showPublishErrorBar, preflightWarnings, publishValidationError]);

  const mergedViolations = useMemo(
    () => mergeValidationDetails(preflightWarnings, serverValidationDetails),
    [preflightWarnings, serverValidationDetails]
  );

  const stepValidationMap = useMemo(
    () => getStepValidationMap(mergedViolations, workflow?.workflow_steps),
    [mergedViolations, workflow?.workflow_steps]
  );

  const edgeValidationMap = useMemo(
    () => getEdgeValidationMap(mergedViolations),
    [mergedViolations]
  );

  const stepValidationKey = useMemo(
    () => JSON.stringify([...stepValidationMap.entries()]),
    [stepValidationMap]
  );

  const edgeValidationKey = useMemo(
    () => JSON.stringify([...edgeValidationMap.entries()]),
    [edgeValidationMap]
  );

  useEffect(() => {
    setNodes((prev) => decorateNodesWithValidation(prev, stepValidationMap));
  }, [stepValidationKey, setNodes, stepValidationMap]);

  useEffect(() => {
    setEdges((prev) => decorateEdgesWithValidation(prev, edgeValidationMap));
  }, [edgeValidationKey, setEdges, edgeValidationMap]);

  const blockingPreflightErrors = useMemo(() => {
    const localErrors = preflightWarnings.filter((w) => (w.severity || 'error') === 'error');
    const serverErrors = serverValidationDetails.filter((d) => (d.severity || 'error') === 'error');
    return [...localErrors, ...serverErrors];
  }, [preflightWarnings, serverValidationDetails]);

  const publishBlocked = hasBlockingPreflightErrors(blockingPreflightErrors);

  const handlePublish = useCallback(async () => {
    if (publishBlocked) {
      setShowPublishErrorBar(true);
      return;
    }
    const scopeTagIds = workflowRef.current?.tag_ids || [];
    if (scopeTagIds.length === 0 && !window.confirm(UNSCOPED_PUBLISH_PROMPT)) {
      return;
    }
    try {
      setShowPublishErrorBar(true);
      setPublishValidationError(null);
      await publishWorkflow();
      setShowPublishErrorBar(false);
    } catch (error) {
      console.error('Failed to publish workflow:', error);
      const messages = error?.errors?.length
        ? error.errors
        : [error?.message || 'Could not publish workflow.'];
      setPublishValidationError({
        messages,
        details: error?.details || [],
        contractVersion: error?.contractVersion,
      });
    }
  }, [publishBlocked, publishWorkflow]);

  const allValidationItems = useMemo(
    () => buildValidationListItems(mergedViolations),
    [mergedViolations]
  );

  const warningItemsForToolbar = useMemo(
    () => dedupeValidationListItems(allValidationItems),
    [allValidationItems]
  );

  const warningItemsForBar = useMemo(
    () =>
      buildPublishBarWarningItems(
        allValidationItems,
        warningItemsForToolbar,
        publishValidationError?.messages
      ),
    [allValidationItems, warningItemsForToolbar, publishValidationError]
  );

  const focusValidationIssue = useCallback(
    (item) => {
      const violation = item?.violation;
      if (!violation) return;

      const target = getViolationFocusTarget(violation, workflow?.workflow_steps);
      if (!target) return;

      if (target.type === 'node') {
        lastClickedNodeRef.current = target.id;
        setSelectedItem({ type: 'node', id: target.id });
        fitView({ nodes: [{ id: target.id }], duration: 300, padding: 0.5 });
        return;
      }

      lastClickedNodeRef.current = null;
      setSelectedItem({
        type: 'edge',
        source: target.source,
        target: target.target,
        actionIndex: target.actionIndex,
        edgeId: target.edgeId,
      });
      fitView({
        nodes: [{ id: target.source }, { id: target.target }],
        duration: 300,
        padding: 0.5,
      });
    },
    [workflow?.workflow_steps, fitView, setSelectedItem, lastClickedNodeRef]
  );

  return {
    stepValidationMap,
    edgeValidationMap,
    warningItemsForToolbar,
    warningItemsForBar,
    showPublishErrorBar,
    publishBlocked,
    handlePublish,
    focusValidationIssue,
  };
}
